import { useMemo, useState } from "react";
import "../styles/setup.css";
import {
  MAX_CAMPAIGN_CODE_QUANTITY,
  MAX_CAMPAIGN_DESIGNATOR_LENGTH,
  MIN_CAMPAIGN_DESIGNATOR_LENGTH,
} from "../constants.js";
import { validateCampaignStep } from "../utils/campaignValidation.js";

const STEPS = ["Campaign", "Quantity", "Review"];

function FieldError({ message }) {
  return message ? <span className="cg-field-error">{message}</span> : null;
}

export default function SetupPage({
  form,
  submitError,
  isSubmitting,
  onFormChange,
  onGenerateDesignator,
  onSubmit,
}) {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const requestedQuantity = Math.max(0, Number(form.requestedCodeQuantity) || 0);
  const stepErrors = useMemo(() => validateCampaignStep(form, step), [form, step]);

  function goNext() {
    const nextErrors = validateCampaignStep(form, step);
    setErrors(nextErrors);
    if (!Object.keys(nextErrors).length) setStep((current) => Math.min(3, current + 1));
  }

  function handleFinalConfirmation() {
    const nextErrors = validateCampaignStep(form, 3);
    setErrors(nextErrors);
    if (!Object.keys(nextErrors).length) onSubmit();
  }

  return (
    <main className="cg-page-shell">
      <div className="cg-page-frame">
        <section className="cg-panel cg-page-card cg-form-panel">
          <div className="cg-panel-header">
            <div>
              <span className="cg-kicker">Campaign Setup</span>
              <h2>Create a campaign</h2>
            </div>
          </div>

          <ol className="cg-wizard-steps" aria-label="Campaign creation progress">
            {STEPS.map((label, index) => {
              const number = index + 1;
              return (
                <li key={label} className={number === step ? "active" : number < step ? "complete" : ""}>
                  <span>{number}</span>
                  {label}
                </li>
              );
            })}
          </ol>

          <form className="cg-campaign-form" onSubmit={(event) => event.preventDefault()} noValidate>
            {step === 1 ? (
              <div className="cg-wizard-panel">
                <div>
                  <span className="cg-kicker">Step 1</span>
                  <h3>Campaign information</h3>
                  <p className="cg-muted-text">Choose the campaign identifier and active dates.</p>
                </div>

                <label>
                  Campaign designator
                  <div className="cg-designator-row">
                    <input
                      name="campaignPrefix"
                      type="text"
                      value={form.campaignPrefix}
                      onChange={onFormChange}
                      placeholder="3–8 letters or numbers"
                      minLength={MIN_CAMPAIGN_DESIGNATOR_LENGTH}
                      maxLength={MAX_CAMPAIGN_DESIGNATOR_LENGTH}
                      aria-invalid={Boolean(errors.campaignPrefix)}
                    />
                    <button type="button" className="cg-secondary-button" onClick={onGenerateDesignator}>
                      Generate
                    </button>
                  </div>
                  <FieldError message={errors.campaignPrefix} />
                </label>

                <label>
                  Campaign name
                  <input
                    name="campaignName"
                    type="text"
                    value={form.campaignName}
                    onChange={onFormChange}
                    placeholder="Cologuard - ABC12"
                    aria-invalid={Boolean(errors.campaignName)}
                  />
                  <FieldError message={errors.campaignName} />
                </label>

                <div className="cg-two-up">
                  <label>
                    Start date
                    <input
                      name="fromDate"
                      type="date"
                      value={form.fromDate}
                      onChange={onFormChange}
                      aria-invalid={Boolean(errors.fromDate)}
                    />
                    <FieldError message={errors.fromDate} />
                  </label>
                  <label>
                    End date (optional)
                    <input
                      name="toDate"
                      type="date"
                      min={form.fromDate}
                      value={form.toDate}
                      onChange={onFormChange}
                      aria-invalid={Boolean(errors.toDate)}
                    />
                    <FieldError message={errors.toDate} />
                  </label>
                </div>
              </div>
            ) : null}

            {step === 2 ? (
              <div className="cg-wizard-panel">
                <div>
                  <span className="cg-kicker">Step 2</span>
                  <h3>Quantity configuration</h3>
                  <p className="cg-muted-text">Choose how many codes should be prepared for this campaign.</p>
                </div>
                <label>
                  Requested quantity
                  <input
                    name="requestedCodeQuantity"
                    type="number"
                    min="1"
                    max={MAX_CAMPAIGN_CODE_QUANTITY}
                    value={form.requestedCodeQuantity}
                    onChange={onFormChange}
                    aria-invalid={Boolean(errors.requestedCodeQuantity)}
                  />
                  <FieldError message={errors.requestedCodeQuantity} />
                </label>
                <div className="cg-generation-plan" aria-live="polite">
                  <strong>{requestedQuantity.toLocaleString()} requested codes</strong>
                  <span>Codes will be prepared automatically after you confirm the campaign.</span>
                  <small>Maximum campaign size: {MAX_CAMPAIGN_CODE_QUANTITY.toLocaleString()} codes.</small>
                  <small>
                    Need more than {MAX_CAMPAIGN_CODE_QUANTITY.toLocaleString()} codes? Contact the
                    Stran team for assistance.
                  </small>
                </div>
              </div>
            ) : null}

            {step === 3 ? (
              <div className="cg-wizard-panel">
                <div>
                  <span className="cg-kicker">Step 3</span>
                  <h3>Review and create</h3>
                  <p className="cg-muted-text">Nothing will start until you click Create campaign.</p>
                </div>
                <div className="cg-review-grid">
                  <div><span>Designator</span><strong>{form.campaignPrefix}</strong></div>
                  <div><span>Campaign</span><strong>{form.campaignName}</strong></div>
                  <div><span>Active dates</span><strong>{form.fromDate} — {form.toDate || "No end date"}</strong></div>
                  <div><span>Requested codes</span><strong>{requestedQuantity.toLocaleString()}</strong></div>
                </div>
                {Object.keys(stepErrors).length ? (
                  <p className="cg-error-text">Return to the previous steps and correct the highlighted information.</p>
                ) : null}
                {submitError ? <p className="cg-error-text">{submitError}</p> : null}
              </div>
            ) : null}

            <div className="cg-wizard-actions">
              {step > 1 ? (
                <button type="button" className="cg-secondary-button" onClick={() => { setErrors({}); setStep((current) => current - 1); }} disabled={isSubmitting}>
                  Back
                </button>
              ) : <span />}
              {step < 3 ? (
                <button type="button" onClick={goNext}>Continue</button>
              ) : (
                <button type="button" onClick={handleFinalConfirmation} disabled={isSubmitting || Object.keys(stepErrors).length > 0}>
                  {isSubmitting ? "Creating campaign..." : "Create campaign"}
                </button>
              )}
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}
